import Box from '@mui/material/Box';
import FormLabel from '@mui/material/FormLabel';
import { RHFAutoCompleteVirtualize } from '@openx/components-core/lib/AutoComplete/RHFAutoCompleteVirtualize.tsx';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection.tsx';
import { RHFRadioField } from '@openx/components-core/lib/RadioField/RHFRadioField.tsx';
import { RHFTextField } from '@openx/components-core/lib/TextField/RHFTextField.tsx';
import { RIGHT_COLUMN_WIDTH } from '@openx/constants/form.ts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { AdvertiserOption } from '../types.ts';
import { getAdvertiserOptionsMap } from '../utils.ts';
import { getStatusOptions } from './utils.tsx';
import type { CreativeFormState } from './validation/validationSchema.ts';

type CreativeDetailsSectionProps = {
  advertiserOptions: AdvertiserOption[];
  isEdit?: boolean;
};

export const CreativeDetailsSection = ({ advertiserOptions, isEdit = false }: CreativeDetailsSectionProps) => {
  const { t } = useTranslation();

  const statusOptions = getStatusOptions(t);

  const { advertiserOptionsMap, advertiserOptionKeys } = useMemo(() => {
    const map = getAdvertiserOptionsMap(advertiserOptions);

    return { advertiserOptionsMap: map, advertiserOptionKeys: Object.keys(map) };
  }, [advertiserOptions]);

  return (
    <FormSection divider title={t('Creative Details')} rightColumnSize={RIGHT_COLUMN_WIDTH}>
      <RHFAutoCompleteVirtualize
        name={'account_uid' satisfies keyof CreativeFormState}
        textFieldProps={{
          label: t('Advertiser'),
          placeholder: t('[ Select or Search Advertiser ]'),
          margin: 'dense',
        }}
        options={advertiserOptionKeys}
        getOptionLabel={option => advertiserOptionsMap[option] || option}
        fullWidth
        data-test="advertiser"
        disabled={isEdit}
      />

      <RHFTextField
        name={'name' satisfies keyof CreativeFormState}
        label={t('Name')}
        placeholder={t('Enter Name')}
        fullWidth
        dataTest="creative-name"
        margin="dense"
      />

      <RHFTextField
        name={'crid' satisfies keyof CreativeFormState}
        label={t('CrID')}
        placeholder={t('Enter Creative ID')}
        fullWidth
        dataTest="creative-crid"
        margin="dense"
        disabled={isEdit}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
        <FormLabel
          sx={{
            typography: 'body1',
            color: 'text.secondary',
            p: 0,
            m: 0,
          }}
        >
          {t('Status')}:
        </FormLabel>
        <RHFRadioField
          name={'status' satisfies keyof CreativeFormState}
          options={statusOptions}
          row
          data-test="creative-status"
        />
      </Box>
    </FormSection>
  );
};
